import { useMemo } from 'react';
import { MasteryRecord } from './useQuizMasteryDB';

interface QuizQuestion {
  id: string;
  subject: string;
  subcategory: string | null;
}

interface ReviewRecommendation {
  questionId: string;
  subject: string;
  subcategory: string | null;
  daysSinceIncorrect: number;
  priority: 'high' | 'medium' | 'low';
}

/**
 * 忘却曲線に基づく復習推奨ロジック
 * 間違えた問題を1日後・3日後・7日後・14日後に再出題を推奨
 */
export function useReviewReminder(
  questions: QuizQuestion[],
  masteryRecord: MasteryRecord
): {
  recommendedQuestions: ReviewRecommendation[];
  totalCount: number;
  subjectBreakdown: Record<string, number>;
} {
  const result = useMemo(() => {
    const now = new Date();
    const recommended: ReviewRecommendation[] = [];
    const subjectBreakdown: Record<string, number> = {};

    questions.forEach((q) => {
      const record = masteryRecord[q.id];
      if (!record || !record.last_incorrect_at || record.incorrect_count === 0) {
        return;
      }

      const lastIncorrectDate = new Date(record.last_incorrect_at);
      const daysSince = Math.floor(
        (now.getTime() - lastIncorrectDate.getTime()) / (1000 * 60 * 60 * 24)
      );

      // 忘却曲線に基づく復習タイミング: 1日後、3日後、7日後、14日後
      const reviewIntervals = [1, 3, 7, 14];
      const shouldReview = reviewIntervals.some((interval) => {
        // 各インターバルの前後1日を許容範囲とする
        return daysSince >= interval && daysSince <= interval + 1;
      });

      if (shouldReview) {
        let priority: 'high' | 'medium' | 'low' = 'low';
        if (daysSince >= 1 && daysSince <= 2) priority = 'high';
        else if (daysSince >= 3 && daysSince <= 4) priority = 'high';
        else if (daysSince >= 7 && daysSince <= 8) priority = 'medium';
        else if (daysSince >= 14 && daysSince <= 15) priority = 'low';

        recommended.push({
          questionId: q.id,
          subject: q.subject,
          subcategory: q.subcategory,
          daysSinceIncorrect: daysSince,
          priority,
        });

        subjectBreakdown[q.subject] = (subjectBreakdown[q.subject] || 0) + 1;
      }
    });

    // 優先度順にソート
    recommended.sort((a, b) => {
      const priorityOrder = { high: 0, medium: 1, low: 2 };
      return priorityOrder[a.priority] - priorityOrder[b.priority];
    });

    return {
      recommendedQuestions: recommended,
      totalCount: recommended.length,
      subjectBreakdown,
    };
  }, [questions, masteryRecord]);

  return result;
}
